import { useMemo } from "react";
import { useLocale } from "./LocaleProvider.jsx";
import { LEAD_PREFERRED_CONTACT_METHODS } from "../utils/leads/leadConstants";
import {
  SELLER_OWNERSHIP_STATUSES,
  SELLER_PREFERRED_CONTACT_METHODS,
  SELLER_TYPES,
} from "../utils/sellerIntake/sellerIntakeConstants";

/**
 * @param {(key: string, params?: object) => string} t
 */
export function buildLeadCaptureCopy(t) {
  return {
    buyerNameLabel: t("forms.fullName"),
    buyerNamePlaceholder: t("forms.leadCapture.buyerNamePlaceholder"),
    buyerPhoneLabel: t("forms.phone"),
    buyerPhonePlaceholder: t("forms.leadCapture.buyerPhonePlaceholder"),
    buyerEmailLabel: t("forms.leadCapture.buyerEmailOptional"),
    buyerEmailPlaceholder: t("forms.leadCapture.buyerEmailPlaceholder"),
    preferredContactLabel: t("forms.preferredContactMethod"),
    messageLabel: t("forms.message"),
    messagePlaceholder: t("forms.leadCapture.messagePlaceholder"),
    consentLabel: t("forms.leadCapture.consentLabel"),
    consentPrivacyNote: t("forms.leadCapture.consentPrivacyNote"),
    cancel: t("common.cancel"),
    close: t("common.close"),
    submitting: t("forms.leadCapture.submitting"),
    editData: t("forms.leadCapture.editData"),
    retry: t("common.retry"),
    contactOptions: [
      { value: LEAD_PREFERRED_CONTACT_METHODS.WHATSAPP, label: t("forms.whatsapp") },
      { value: LEAD_PREFERRED_CONTACT_METHODS.PHONE, label: t("forms.call") },
      { value: LEAD_PREFERRED_CONTACT_METHODS.EMAIL, label: t("forms.emailOption") },
      { value: LEAD_PREFERRED_CONTACT_METHODS.ANY, label: t("forms.anyContact") },
    ],
  };
}

export function useLeadCaptureCopy() {
  const { t } = useLocale();
  return useMemo(() => buildLeadCaptureCopy(t), [t]);
}

/**
 * @param {(key: string, params?: object) => string} t
 */
export function buildSellerIntakeCopy(t) {
  return {
    sectionTitle: t("forms.sellerIntake.sectionTitle"),
    sectionIntro: t("automotive.sellYourCarDescription"),
    contactSectionTitle: t("forms.sellerIntake.contactSectionTitle"),
    vehicleSectionTitle: t("forms.sellerIntake.vehicleSectionTitle"),
    pricingSectionTitle: t("forms.sellerIntake.pricingSectionTitle"),
    conditionSectionTitle: t("forms.sellerIntake.conditionSectionTitle"),
    consentSectionTitle: t("forms.sellerIntake.consentSectionTitle"),
    submitLabel: t("automotive.submitVehicle"),
    submitPendingLabel: t("automotive.submitting"),
    requiredMarker: `(${t("forms.required")})`,
    formSectionSteps: [
      t("forms.sellerIntake.contactSectionTitle"),
      t("forms.sellerIntake.vehicleSectionTitle"),
      t("forms.sellerIntake.pricingSectionTitle"),
      t("forms.sellerIntake.conditionSectionTitle"),
      t("forms.sellerIntake.consentSectionTitle"),
    ],
    consentLabel: t("forms.sellerIntake.consentLabel"),
    consentNote: t("forms.sellerIntake.consentNote"),
    privacyPolicyLink: t("forms.sellerIntake.privacyPolicyLink"),
    sellerTypeOptions: [
      { value: SELLER_TYPES.INDIVIDUAL, label: t("forms.sellerIntake.sellerTypeIndividual") },
      { value: SELLER_TYPES.DEALER, label: t("forms.sellerIntake.sellerTypeDealer") },
      { value: SELLER_TYPES.SMALL_LOT, label: t("forms.sellerIntake.sellerTypeSmallLot") },
      { value: SELLER_TYPES.UNKNOWN, label: t("forms.sellerIntake.sellerTypeUnknown") },
    ],
    contactMethodOptions: [
      { value: SELLER_PREFERRED_CONTACT_METHODS.WHATSAPP, label: t("forms.whatsapp") },
      { value: SELLER_PREFERRED_CONTACT_METHODS.PHONE, label: t("forms.call") },
      { value: SELLER_PREFERRED_CONTACT_METHODS.EMAIL, label: t("forms.emailOption") },
      { value: SELLER_PREFERRED_CONTACT_METHODS.ANY, label: t("forms.anyContact") },
    ],
    ownershipOptions: [
      { value: SELLER_OWNERSHIP_STATUSES.OWNER, label: t("forms.sellerIntake.ownershipOwner") },
      { value: SELLER_OWNERSHIP_STATUSES.DEALER_INVENTORY, label: t("forms.sellerIntake.ownershipDealerInventory") },
      { value: SELLER_OWNERSHIP_STATUSES.CONSIGNMENT, label: t("forms.sellerIntake.ownershipConsignment") },
      { value: SELLER_OWNERSHIP_STATUSES.UNKNOWN, label: t("forms.sellerIntake.ownershipUnknown") },
    ],
  };
}

export function useSellerIntakeCopy() {
  const { t } = useLocale();
  return useMemo(() => buildSellerIntakeCopy(t), [t]);
}
